import React, { useEffect, useState, useCallback, useMemo } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import {
  getMenuList, createMenu, updateMenu, deleteMenu,
  getMenuListByRole, grantRoleMenu, revokeRoleMenu,
} from '../../api/menu';
import { getAdminRoleList } from '../../api/account';

export default function MenuManagePage() {
  const { auth } = useAuth();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [grantedMenuIds, setGrantedMenuIds] = useState(new Set());

  const fetchMenus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getMenuList();
      setMenus(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || '메뉴 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMenus(); }, [fetchMenus]);
  useEffect(() => {
    getAdminRoleList()
      .then((res) => {
        setRoles(res.data || []);
        if (res.data?.length) setSelectedRole(res.data[0].roleCode);
      })
      .catch(() => setRoles([]));
  }, []);

  const fetchGranted = useCallback(async (roleCode) => {
    if (!roleCode) return;
    try {
      const res = await getMenuListByRole(roleCode);
      setGrantedMenuIds(new Set((res.data || []).map((m) => m.menuId)));
    } catch {
      setGrantedMenuIds(new Set());
    }
  }, []);

  useEffect(() => { fetchGranted(selectedRole); }, [selectedRole, fetchGranted]);

  const toggleGrant = async (menuId, granted) => {
    try {
      if (granted) {
        await revokeRoleMenu(selectedRole, menuId);
      } else {
        await grantRoleMenu(selectedRole, menuId);
      }
      fetchGranted(selectedRole);
    } catch (err) {
      window.alert(err.response?.data?.message || '권한 변경에 실패했습니다.');
    }
  };

  const handleCreate = async (form) => {
    try {
      await createMenu({ ...form, createdBy: auth.adminName });
      setCreating(false);
      fetchMenus();
    } catch (err) {
      window.alert(err.response?.data?.message || '메뉴 등록에 실패했습니다.');
    }
  };

  const handleUpdate = async (form) => {
    try {
      await updateMenu(editing.menuId, { ...form, updatedBy: auth.adminName });
      setEditing(null);
      fetchMenus();
    } catch (err) {
      window.alert(err.response?.data?.message || '메뉴 수정에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMenu(deleteTarget.menuId);
      setDeleteTarget(null);
      fetchMenus();
    } catch (err) {
      window.alert(err.response?.data?.message || '메뉴 삭제에 실패했습니다.');
    }
  };

  // flat 메뉴 목록을 2단 트리로 조립한다(최상위 = parent_menu_id 없음, 그 아래 자식).
  const tree = useMemo(() => {
    const top = menus.filter((m) => m.parentMenuId == null).sort((a, b) => a.sortOrder - b.sortOrder);
    return top.map((p) => ({
      ...p,
      children: menus
        .filter((c) => Number(c.parentMenuId) === Number(p.menuId))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    }));
  }, [menus]);

  return (
    <AdminLayout title="메뉴관리" menuUrl="/admin/menu">
      <div className="page-header">
        <div>
          <h1>메뉴관리</h1>
          <div className="subtitle">관리자 사이트 메뉴와 역할별 접근 권한을 관리합니다.</div>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ 메뉴 추가</button>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>순서</th><th>메뉴명</th><th>URL</th><th>사용여부</th><th>관리</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={5} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && menus.length === 0 && <tr><td colSpan={5} className="empty-row">등록된 메뉴가 없습니다.</td></tr>}
              {!loading && !error && tree.map((p) => {
                const isGroup = p.children.length > 0;
                const rowFor = (m, child) => (
                  <tr key={m.menuId} style={child ? undefined : { background: 'var(--color-surface-alt)' }}>
                    <td>{m.sortOrder}</td>
                    <td style={child ? { paddingLeft: 28 } : { fontWeight: 700 }}>
                      {child ? <span style={{ color: 'var(--color-text-tertiary)', marginRight: 6 }}>└</span> : null}
                      {m.menuName}
                    </td>
                    <td className="text-secondary">{m.menuUrl || (isGroup && !child ? '(대분류)' : '')}</td>
                    <td><StatusBadge value={m.useYn} /></td>
                    <td className="actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(m)}>수정</button>
                      <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(m)}>삭제</button>
                    </td>
                  </tr>
                );
                return (
                  <React.Fragment key={p.menuId}>
                    {rowFor(p, false)}
                    {p.children.map((c) => rowFor(c, true))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card card-padded">
        <div className="flex-between" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 15 }}>역할별 메뉴 권한</h2>
          <select className="select" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
            {roles.map((r) => <option key={r.roleCode} value={r.roleCode}>{r.roleName} ({r.roleCode})</option>)}
          </select>
        </div>
        {tree.map((node) => {
          // 대분류(자식 있음)는 소제목으로, 자식들을 체크박스로. 단독 최상위(대시보드 등)는 그대로 한 줄.
          const leaves = node.children.length ? node.children : [node];
          return (
            <div key={node.menuId} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                {node.menuName}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {leaves.map((m) => {
                  const granted = grantedMenuIds.has(m.menuId);
                  return (
                    <label key={m.menuId} className="checkbox-row" style={{ fontSize: 13.5 }}>
                      <input type="checkbox" checked={granted} onChange={() => toggleGrant(m.menuId, granted)} />
                      {m.menuName}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {creating && <MenuFormModal menus={menus} onClose={() => setCreating(false)} onSave={handleCreate} />}
      {editing && <MenuFormModal menus={menus} menu={editing} onClose={() => setEditing(null)} onSave={handleUpdate} />}

      {deleteTarget && (
        <ConfirmDialog
          title="메뉴 삭제"
          message={`'${deleteTarget.menuName}' 메뉴를 삭제하시겠습니까?`}
          confirmLabel="삭제"
          danger
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </AdminLayout>
  );
}

function MenuFormModal({ menu, menus, onClose, onSave }) {
  const isNew = !menu;
  const [form, setForm] = useState({
    parentMenuId: menu?.parentMenuId || '',
    menuName: menu?.menuName || '',
    menuUrl: menu?.menuUrl || '',
    menuIcon: menu?.menuIcon || '',
    sortOrder: menu?.sortOrder ?? 0,
    useYn: menu?.useYn || 'Y',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...form, parentMenuId: form.parentMenuId || null, sortOrder: Number(form.sortOrder) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isNew ? '메뉴 추가' : '메뉴 수정'}
      onClose={onClose}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </>}
    >
      <form onSubmit={submit}>
        <div className="field">
          <label>메뉴명</label>
          <input className="input" value={form.menuName} onChange={(e) => setForm({ ...form, menuName: e.target.value })} required />
        </div>
        <div className="field">
          <label>URL</label>
          <input className="input" value={form.menuUrl} onChange={(e) => setForm({ ...form, menuUrl: e.target.value })} />
        </div>
        <div className="field">
          <label>상위 메뉴</label>
          <select className="select" value={form.parentMenuId || ''} onChange={(e) => setForm({ ...form, parentMenuId: e.target.value })}>
            <option value="">(최상위 메뉴)</option>
            {menus
              .filter((m) => m.parentMenuId == null && m.menuId !== menu?.menuId)
              .map((m) => (
                <option key={m.menuId} value={m.menuId}>{m.menuName}</option>
              ))}
          </select>
        </div>
        <div className="field-row">
          <div className="field">
            <label>정렬순서</label>
            <input className="input" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
          </div>
          <div className="field">
            <label>사용여부</label>
            <select className="select" value={form.useYn} onChange={(e) => setForm({ ...form, useYn: e.target.value })}>
              <option value="Y">사용</option>
              <option value="N">미사용</option>
            </select>
          </div>
        </div>
      </form>
    </Modal>
  );
}
